// src/receptionist/screens/Bookings.tsx
//
// The queue, and the conversation behind every row. In v1 the staff WhatsApp
// group is the working inbox (PRD §3.2); this screen mirrors that queue so a
// merchant who prefers a desktop can confirm here instead. Confirming in
// either place sends the customer the same message.
//
// The transcript reads from the SHOP's side: the customer on the left, the AI
// on the right. That is the opposite of the customer's own phone, and it is
// deliberate — this is the merchant's view of their own inbox.
//
// Wired to the bookings API (ourteam-frontend#3609): the queue, the counts and
// the transcripts come from listBookings(), and Confirm / Decline go through
// the server, which is what actually messages the customer.

import { useCallback, useEffect, useRef, useState } from "react";
import { AlertTriangleIcon, ArrowLeftIcon, CalendarCheckIcon, ChevronRightIcon, Loader2Icon, SearchXIcon } from "lucide-react";
import { PageHeader, Button, EmptyState } from "../components/kit";
import { FilterTabs, TagPill } from "../components/ui";
import { InfoNotice } from "../components/kit";
import { SURFACE, TYPE, type Tone } from "@oura/ui";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@component/table";
import { BookingDrawer } from "../components/BookingDrawer";
import { cn } from "@oura/lib/cn";
import { Link, useSearchParams } from "react-router-dom";
import { Pagination } from "@oura/primitives/Pagination";
import { useSnackbar } from "@hook/use-snackbar";
import { listBookings, type Booking, type BookingsPage, type BookingsFilter } from "@service/receptionist";
import { slotWhen, type BookingStatus } from "../bookings";

type Filter = BookingsFilter;
const PAGE_SIZE = 50;

/** The 404 the API sends for a merchant the token cannot see — never a 403,
 *  which would confirm the merchant exists. */
function isNotFound(err: unknown): boolean {
    return (err as { response?: { status?: number } } | null)?.response?.status === 404;
}

type Load =
    | { kind: "loading" }
    | { kind: "notFound" }
    | { kind: "error" }
    | { kind: "ready"; page: BookingsPage };

/** Pending waits on the team (amber, as Oura's "theirs to move"), Confirmed is done, the rest closed. */
const STATUS_TONE: Record<BookingStatus, Tone> = {
    Pending: "amber",
    Confirmed: "green",
    Declined: "gray",
    Cancelled: "gray",
};


export function BookingsScreen({
    merchantId,
    profileHref,
    backHref,
}: {
    merchantId: string | null;
    /** Where the template pack lives — offered when a confirm is refused for
     *  want of an approved template. */
    profileHref: string;
    /** The roll-up, on the staff drill-in; nothing for a client, whose own
     *  merchant being unavailable has no "back". */
    backHref?: string;
}) {
    const snackbar = useSnackbar();
    // Overview links here with ?status=pending or ?open=<id> (#4847): read once, on arrival.
    const [params] = useSearchParams();
    const [filter, setFilterState] = useState<Filter>(() => {
        const s = params.get("status");
        return s === "pending" || s === "confirmed" || s === "closed" ? s : "all";
    });
    const [page, setPage] = useState(1);
    const [state, setState] = useState<Load>({ kind: "loading" });
    /** The booking the drawer is showing, by id. Null is closed. */
    const [openId, setOpenId] = useState<string | null>(() => params.get("open"));
    const seq = useRef(0);

    const load = useCallback(async (quiet = false) => {
        const mine = ++seq.current;
        if (!merchantId) { setState({ kind: "notFound" }); return; }
        if (!quiet) setState((cur) => (cur.kind === "ready" ? cur : { kind: "loading" }));
        try {
            // `closed` (Declined OR Cancelled) is the server's filter, and the
            // counts cover the whole queue, so the tabs stay right on page 2.
            const result = await listBookings(merchantId, { status: filter, page, limit: PAGE_SIZE });
            if (seq.current !== mine) return;
            setState({ kind: "ready", page: result });
        } catch (err) {
            if (seq.current !== mine) return;
            setState({ kind: isNotFound(err) ? "notFound" : "error" });
        }
    }, [merchantId, filter, page]);

    useEffect(() => { void load(); }, [load]);

    const setFilter = (f: Filter) => { setFilterState(f); setPage(1); };


    /**
     * The row is replaced from the server's response. The customer-facing
     * wording is the server's to own — it is the same message a staff "1"
     * sends — and a second copy composed here is how the two would drift.
     */
    const replaceRow = (booking: Booking) =>
        setState((cur) => cur.kind === "ready"
            ? { ...cur, page: { ...cur.page, bookings: cur.page.bookings.map((b) => (b.id === booking.id ? booking : b)) } }
            : cur);


    const counts = state.kind === "ready" ? state.page.counts : { all: 0, pending: 0, confirmed: 0, closed: 0 };

    return (
        <div>
            <PageHeader
                title="Bookings"
                subtitle="Every request with the conversation that produced it. Open a row to read the thread."
            />

            <InfoNotice className="mb-5">
                Your team can confirm here or by replying in the staff WhatsApp group. Either way the
                customer gets the same message.
            </InfoNotice>

            {state.kind === "notFound" ? (
                <div className={SURFACE.card}>
                <EmptyState
                    icon={SearchXIcon}
                    title="This merchant isn't available to you"
                    description="Either it does not exist or your account is not linked to it."
                    action={backHref ? (
                        <Link to={backHref} className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:underline">
                            <ArrowLeftIcon className="h-4 w-4" /> Back to merchants
                        </Link>
                    ) : undefined}
                />
                </div>
            ) : state.kind === "error" ? (
                <div className={SURFACE.card}>
                <EmptyState
                    icon={AlertTriangleIcon}
                    title="Couldn't load the bookings"
                    description="Something went wrong asking for the queue."
                    action={<Button onClick={() => void load()}>Try again</Button>}
                />
                </div>
            ) : (
                <>
                    <div className="mb-5">
                        <FilterTabs<Filter>
                            value={filter}
                            onChange={setFilter}
                            options={[
                                { value: "all", label: "All", count: counts.all },
                                { value: "pending", label: "Pending", count: counts.pending, tone: "amber" },
                                { value: "confirmed", label: "Confirmed", count: counts.confirmed, tone: "green" },
                                { value: "closed", label: "Closed", count: counts.closed, tone: "gray" },
                            ]}
                        />
                    </div>

                    <div className={cn(SURFACE.card, "overflow-hidden")}>
                    {state.kind === "loading" ? (
                        <div className="flex justify-center py-16">
                            <Loader2Icon className="h-5 w-5 animate-spin text-gray-400" />
                        </div>
                    ) : state.page.bookings.length === 0 ? (
                        <EmptyState icon={CalendarCheckIcon} title="Nothing here" description="No bookings match this filter." />
                    ) : (
                        <div className="overflow-x-auto">
                        {/* The Oura list table: sentence-case headers, hairlines, a grey wash on hover. */}
                        <Table className="w-full min-w-max">
                            <TableHeader>
                                <TableRow className="border-b border-gray-100 hover:bg-transparent">
                                    {["Customer", "Service", "Slot", "Status"].map((h) => (
                                        <TableHead key={h} className={cn(TYPE.tc, "h-12 px-5 py-3 text-left font-medium text-gray-500")}>
                                            {h}
                                        </TableHead>
                                    ))}
                                    <TableHead className="h-12 w-10 px-3 py-3"><span className="sr-only">Open</span></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody className="divide-y divide-gray-100">
                                {state.page.bookings.map((b) => (
                                    <TableRow
                                        key={b.id}
                                        onClick={() => setOpenId(b.id)}
                                        tabIndex={0}
                                        role="button"
                                        aria-haspopup="dialog"
                                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpenId(b.id); } }}
                                        className="group cursor-pointer border-0 hover:bg-gray-50 focus-visible:bg-gray-50 focus-visible:outline-none"
                                    >
                                        <TableCell className="whitespace-nowrap px-5 py-3">
                                            <span className="block text-sm text-gray-900 group-hover:text-blue-600">{b.customer}</span>
                                            <span className={cn(TYPE.tc, "block text-gray-500")}>{b.phone}</span>
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap px-5 py-3 text-sm text-gray-700">{b.service}</TableCell>
                                        {/* The stored moment (slot_at), in the merchant's time zone; the customer's
                                            own words under it, since "tomorrow" was only true on the day (#4798). */}
                                        <TableCell className="whitespace-nowrap px-5 py-3">
                                            {(() => {
                                                const when = slotWhen(b);
                                                if (!when) {
                                                    return (
                                                        <>
                                                            <span className="block text-sm text-gray-700">{b.slot || "—"}</span>
                                                            {b.slot && <span className={cn(TYPE.tc, "block text-gray-400")}>No date yet</span>}
                                                        </>
                                                    );
                                                }
                                                return (
                                                    <>
                                                        <span className="block text-sm text-gray-900">{when.date}, {when.time}</span>
                                                        {b.slot && <span className={cn(TYPE.tc, "block text-gray-500")}>Customer said: {b.slot}</span>}
                                                    </>
                                                );
                                            })()}
                                        </TableCell>
                                        <TableCell className="px-5 py-3">
                                            <span className="flex flex-wrap gap-1.5">
                                                <TagPill tone={STATUS_TONE[b.status] ?? "gray"}>{b.status}</TagPill>
                                                {(b.tags ?? []).map((tag) => <TagPill key={tag}>{tag}</TagPill>)}
                                            </span>
                                        </TableCell>
                                        <TableCell className="px-3 py-3">
                                            <ChevronRightIcon aria-hidden className="h-3.5 w-3.5 text-gray-400" />
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        </div>
                    )}
                    </div>

                    {state.kind === "ready" && (
                        <Pagination
                            page={state.page.pagination.page}
                            total={state.page.pagination.total}
                            pageSize={state.page.pagination.limit}
                            itemLabel="bookings"
                            onPageChange={setPage}
                            className="mt-4"
                        />
                    )}
                </>
            )}

            <BookingDrawer
                booking={state.kind === "ready" ? state.page.bookings.find((b) => b.id === openId) ?? null : null}
                onClose={() => setOpenId(null)}
                onDecline={(booking) => { replaceRow(booking); void load(true); }}
                onStale={(message) => { snackbar.showError(message); void load(true); }}
                merchantId={merchantId}
                onSent={replaceRow}
                profileHref={profileHref}
            />
        </div>
    );
}
